import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import type { SvgIconProps, SvgIconTypeMap } from "@mui/material";
import { Grid, Stack, Typography } from "@mui/material";
import type { ComponentType, JSX, PropsWithChildren } from "react";
import { useCallback, useMemo } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import type { ReportValidationResponse } from "../../../../../../api/types/svTypes.ts";
import { CompanyMultipleReportsListItem } from "./CompanyMultipleReportsListItem";
import { Failed } from "./controls/Failed";
import { InProgressIcon } from "./controls/InProgressIcon";
import { ReportInProgress } from "./controls/ReportInProgress";
import ReportIsNotValid from "./controls/ReportIsNotValid";
import Validated from "./controls/Validated";
import { WaitingForValidation } from "./controls/WaitingForValidation";
import type { SelectedCompany, SelectedCompanyReport } from "./hooks/SelectedCompany";
import type { ReportValidationType } from "./hooks/useReportValidation";
import { getInvalidFieldsCount } from "./utils/getInvalidFieldsCount";
import { getItemSx } from "./utils/getItemSx";

interface Props {
  company: SelectedCompany;
  activeCompany: SelectedCompany | undefined;
  activeCompanyReport: SelectedCompanyReport | undefined;
  availableCompanies: ListItemData[];
  validation: ReportValidationType;
  onSelectedCompanyReport: (companyReport: SelectedCompanyReport | undefined) => void;
}

export function CompanyMultipleReportsListItemContainer({
  company,
  activeCompany,
  activeCompanyReport,
  availableCompanies,
  validation,
  onSelectedCompanyReport,
}: Props) {
  const isReportActive = useCallback(
    (report: SelectedCompanyReport) => {
      return (
        activeCompany?.code === report.companyCode && report.report.reportId === activeCompanyReport?.report.reportId
      );
    },
    [activeCompany?.code, activeCompanyReport?.report.reportId]
  );

  const isValidating = useMemo(() => company.reports.some((r) => r.validating), [company.reports]);

  return (
    <CompanyMultipleReportsListItem
      key={company.code}
      company={company}
      companyBranding={availableCompanies.find((c) => c.value === company.code)}
      Action={
        <CloseIconButton
          onClick={() => validation.removeCompany(company.code)}
          color="secondary"
          iconProps={{ sx: (theme) => ({ color: theme.palette.secondary.light }) }}
        />
      }
      Summary={isValidating ? <ValidatingInfo items={company.reports} /> : <ValidatedInfo items={company.reports} />}
    >
      {company.reports.map((report) => (
        <ValidationItem
          key={report.report.reportId}
          report={report}
          active={isReportActive(report)}
          validation={validation}
          onSelected={() => onSelectedCompanyReport(report)}
          TopAction={
            <CloseIconButton
              onClick={() => validation.removeReport(report)}
              color="secondary"
              iconProps={{ sx: (theme) => ({ color: theme.palette.secondary.light }) }}
            />
          }
        />
      ))}
    </CompanyMultipleReportsListItem>
  );
}

interface ItemProps {
  report: SelectedCompanyReport;
  active: boolean;
  validation: ReportValidationType;
  TopAction?: JSX.Element;
  onSelected: () => void;
}

function ValidationItem({ report, active, validation, onSelected, TopAction }: ItemProps) {
  return (
    <ValidationItemContainer
      caption={report.report.name}
      active={active}
      completed={report.validated}
      selectable
      onSelected={onSelected}
      TopAction={TopAction}
    >
      {!report.validating && !report.validated && !report.error && <WaitingForValidation />}
      {report.validating && (
        <ReportInProgress text="Please wait while we check if the report can be copied within the company..." />
      )}
      {report.validated && report.result?.hasRequiredDataset === false && (
        <ReportIsNotValid
          errorMessage={`Dataset ${report.result?.requiredDataSetName ?? ""} is missing in the target company.`}
        />
      )}
      {report.validated && report.result?.hasRequiredDataset && <Validated companyReport={report} />}
      {report.error && <Failed onRetry={() => validation.retryValidation(report)} />}
    </ValidationItemContainer>
  );
}

interface ValidationItemContainerProps {
  caption: string;
  active?: boolean;
  completed: boolean;
  selectable?: boolean;
  BottomAction?: JSX.Element;
  TopAction?: JSX.Element;
  onSelected?: () => void;
}
export function ValidationItemContainer({
  caption,
  active,
  completed,
  selectable,
  children,
  BottomAction,
  TopAction,
  onSelected,
}: PropsWithChildren<ValidationItemContainerProps>) {
  return (
    <Grid
      container
      sx={(theme) => ({
        bgcolor: "background.paper",
        p: 1,
        gap: 1,
        borderRadius: 1,
        border: "1px solid",
        width: "100%",
        ...getItemSx(selectable, active, !completed, theme),
      })}
      onClick={onSelected}
    >
      <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
        <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between", alignItems: "center" }}>
          <Typography> {caption}</Typography>
          {TopAction}
        </Stack>
        <Stack direction="row" sx={{ height: 20, position: "relative" }}>
          {children}
        </Stack>
      </Stack>
      {BottomAction}
    </Grid>
  );
}

export function ValidatingInfo({
  items,
}: {
  items: {
    validated: boolean;
    result?: ReportValidationResponse;
    error?: boolean;
  }[];
}) {
  const validatedCount = useMemo(() => {
    return items.filter((r) => r.validated || r.error || r.result?.hasRequiredDataset === false).length;
  }, [items]);

  return (
    <Stack direction={"row"} spacing={1}>
      <Typography color={"secondary"}>
        {validatedCount}/{items.length}
      </Typography>
      <InProgressIcon />
    </Stack>
  );
}
export function ValidatedInfo({
  items,
}: {
  items: {
    validating: boolean;
    validated: boolean;
    result?: ReportValidationResponse;
    error?: boolean;
  }[];
}) {
  const failedCount = useMemo(() => {
    return items.filter((r) => r.error || r.result?.hasRequiredDataset === false).length;
  }, [items]);

  const warningCount = useMemo(() => {
    return items.filter((r) => getInvalidFieldsCount(r.result) > 0).length;
  }, [items]);

  const successCount = useMemo(() => {
    return items.filter(
      (r) =>
        r.validated &&
        getInvalidFieldsCount(r.result) === 0 &&
        !r.error &&
        ((r.result && r.result?.hasRequiredDataset) || !r.result)
    ).length;
  }, [items]);

  return (
    <Stack direction="row" spacing={1.5}>
      <ValidationInfoItem count={failedCount} Icon={ErrorRoundedIcon} color="error" />
      <ValidationInfoItem count={warningCount} Icon={WarningRoundedIcon} color="warning" />
      <ValidationInfoItem count={successCount} Icon={CheckRoundedIcon} color="success" />
    </Stack>
  );
}

export function ValidationInfoItem({
  count,
  Icon,
  color,
}: {
  count: number | string;
  Icon: ComponentType<SvgIconProps>;
  color?: SvgIconTypeMap["props"]["color"];
}) {
  return count ? (
    <Stack direction="row" spacing={0.2} sx={{ alignItems: "center" }}>
      <Typography variant="caption" color="secondary">
        {count}
      </Typography>
      <Icon color={color} />
    </Stack>
  ) : null;
}
